import ErrorBoundary from "@/components/common/ErrorBoundary";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/datatable/components/DataTable";
import { safeDecompress, tableSearchSchema } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useConfirm from "@/hooks/useConfirm";
import { ateliersKeys } from "@/query-keys";
import type { AtelierListItem } from "@/schemas/atelierSchema";
import { services } from "@/services";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import NewAtelier from "./_components/NewAtelier";
import useAtelierMutation from "./_hooks/useAtelierMutation";
import useAteliersQuery from "./_hooks/useAteliersQuery";
import useColumns from "./_hooks/useColumns";

export const Route = createFileRoute("/_private/ateliers/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: ateliersKeys.lists(deps.q),
      queryFn: () => services.getAteliers(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => (
    <ErrorBoundary error={error} reset={() => window.location.reload()} />
  ),
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const loaderData = Route.useLoaderData();

  const columns = useColumns();
  const { t } = useTranslation();

  const { data: paginatedAteliers } = useAteliersQuery(search.q, loaderData);
  const { deleteAtelier, isDeleting } = useAtelierMutation();

  const deleteConfirm = useConfirm({
    title: t("ateliers.confirm.delete.title"),
    description: t("ateliers.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleView = useCallback(
    (atelier: AtelierListItem) =>
      navigate({ to: "/ateliers/$atelierId", params: { atelierId: atelier.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (atelier: AtelierListItem) => deleteConfirm.confirm(() => void deleteAtelier(atelier.id)),
    [deleteConfirm, deleteAtelier],
  );

  return (
    <>
      <PageHeader
        title={t("ateliers.list.title")}
        subtitle={t("ateliers.list.subtitle")}
        action={<NewAtelier />}
      />
      <DataTable<AtelierListItem>
        searchPlaceholder={t("ateliers.list.searchPlaceholder")}
        columns={columns}
        data={paginatedAteliers?.items}
        nextCursor={paginatedAteliers?.nextCursor}
        hasMore={paginatedAteliers?.hasMore}
        queryKey={ateliersKeys.lists(search.q)}
        navigateFrom="/ateliers"
        searchFrom="/_private/ateliers/"
        onView={handleView}
        onDelete={handleDelete}
        searchable={true}
      />
      {deleteConfirm.confirmDialog}
    </>
  );
}
